import { type PathParams } from "@constellation-academy/epic-ui";
import React from "react";
import { useLazyLoadQuery } from "react-relay";
import { useNavigate, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import { useTranslation } from "@i18n/hooks";
import { type reportDetails_Query } from "@relay/reportDetails_Query.graphql";
import { QUERY } from "./report-details.grapqhl";
import { HeaderWrapper } from "./report-details.styles";
import { getProblemTypeTranslations, getReportTypeTranslations } from "./report-details.utils";
import { BaseScreen } from "../../components/base-screen";
import { Button } from "../../components/button";
import { DataTable } from "../../components/data-table";
import { formatDateTime } from "../../components/DateTimeDisplay";
import { Paths } from "../../routes/paths";
import { type ReportsPath } from "../reports/reports.paths";

export const ReportDetailsScreen = () => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const { reportId } = useParams<PathParams<typeof ReportsPath>>();
	const { node } = useLazyLoadQuery<reportDetails_Query>(QUERY, {
		reportId: reportId!,
		skip: !reportId,
	});
	if (!reportId) {
		navigate(Paths.reports.path);
		return;
	}

	const anonymousFallback = t("reports.anonymous");
	const notProvidedFallback = "-";
	const rows: Array<{ label: string; value: string }> = [
		{
			label: t("reports.referenceNumber"),
			value: node?.referenceNumber ?? notProvidedFallback,
		},
		{
			label: t("reports.createdAt"),
			value: node?.createdAt ? formatDateTime(node?.createdAt) : notProvidedFallback,
		},
		{ label: t("firstName"), value: node?.data?.firstName ?? anonymousFallback },
		{ label: t("lastName"), value: node?.data?.lastName ?? anonymousFallback },
		{ label: t("email"), value: node?.data?.email ?? anonymousFallback },
		{
			label: t("reports.reportType"),
			value: getReportTypeTranslations(node?.data?.reportType) || notProvidedFallback,
		},
		{
			label: t("reports.problemType"),
			value: getProblemTypeTranslations(node?.data?.problemType) || notProvidedFallback,
		},
		{ label: t("reports.url"), value: node?.data?.url ?? "" },
		{ label: t("reports.extra"), value: node?.data?.extra ?? notProvidedFallback },
	];
	const handleOnClick = () => {
		navigate(-1);
	};
	return (
		<BaseScreen>
			<DataTable
				emptyMessage={
					<div className="flex justify-content-center align-items-center">
						<div className="mr-2">{t("reports.noReportsFound")}</div>
					</div>
				}
				header={
					<HeaderWrapper>
						<h1>
							{t("reports.reportTitle")} [{node?.referenceNumber}]
						</h1>
						<Button label={t("actions.back")} onClick={handleOnClick} />
					</HeaderWrapper>
				}
				className="mb-3"
				value={rows}
			>
				{(Column) => (
					<>
						<Column header="" field="label" />
						<Column
							header=""
							body={(row) => {
								return match(row.label)
									.with(t("email"), () =>
										row.value === notProvidedFallback ? (
											notProvidedFallback
										) : (
											<a href={`mailto:${row.value}`}>{row.value}</a>
										),
									)
									.with(t("reports.url"), () =>
										row.value ? (
											<a href={row.value} target={"_blank"} rel="noreferrer">
												{row.value}
											</a>
										) : (
											<div>{notProvidedFallback}</div>
										),
									)
									.otherwise(() => row.value);
							}}
						/>
					</>
				)}
			</DataTable>
		</BaseScreen>
	);
};
